import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Trash2 } from "lucide-react";
import ModalShell from "../modal/ModalShell";
import AnnouncementAudienceSummary from "./AnnouncementAudienceSummary";
import AnnouncementMessageFields from "./AnnouncementMessageFields";
import AnnouncementPersonalization from "./AnnouncementPersonalization";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { AnnouncementAudience } from "../../features/announcements/contracts/AnnouncementAudience";
import {
  selectAnnouncementSendStatus,
  selectMessageTemplates,
} from "../../features/announcements/announcements-slice";
import {
  createMessageTemplate,
  deleteMessageTemplate,
  fetchMessageTemplates,
  searchAnnouncementSessions,
  sendAnnouncement,
  updateMessageTemplate,
} from "../../features/announcements/slice-extensions/announcements-slice-extension";
import { SessionSearchResultResponse } from "../../features/search/contracts/SessionSearchResultResponse";

interface Props {
  onClose: () => void;
  /** Pre-selects the session-roster audience (the shortcut from a session detail). */
  initialSession?: { id: string; title: string };
}

/** Compose and send a bulk email to all members or one session's roster. */
const AnnouncementComposeDialog = ({ onClose, initialSession }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const templates = useAppSelector(selectMessageTemplates);
  const sendStatus = useAppSelector(selectAnnouncementSendStatus);

  const [audience, setAudience] = useState<AnnouncementAudience>(
    initialSession ? "Session" : "AllMembers",
  );
  const [session, setSession] = useState<{ id: string; title: string } | null>(
    initialSession ?? null,
  );
  const [sessionQuery, setSessionQuery] = useState("");
  const [sessionResults, setSessionResults] = useState<SessionSearchResultResponse[]>([]);
  const [templateId, setTemplateId] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [templateName, setTemplateName] = useState("");

  useEffect(() => {
    dispatch(fetchMessageTemplates());
  }, [dispatch]);

  // Debounced session search while the instructor types.
  useEffect(() => {
    const query = sessionQuery.trim();
    if (audience !== "Session" || query.length < 2) {
      setSessionResults([]);
      return;
    }
    const handle = setTimeout(async () => {
      try {
        const results = await dispatch(searchAnnouncementSessions(query)).unwrap();
        setSessionResults(results);
      } catch {
        setSessionResults([]);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [audience, sessionQuery, dispatch]);

  const applyTemplate = (id: string) => {
    setTemplateId(id);
    const template = templates.find((candidate) => candidate.id === id);
    if (template) {
      setSubject(template.subject);
      setBody(template.body);
      setTemplateName(template.name);
    }
  };

  const busy = sendStatus === "loading";
  const canSend =
    !busy &&
    subject.trim().length > 0 &&
    body.trim().length > 0 &&
    (audience === "AllMembers" || session !== null) &&
    (!saveAsTemplate || templateName.trim().length > 0);

  const persistTemplate = async () => {
    const name = templateName.trim();
    const existing = templates.find(
      (candidate) => candidate.name.toLowerCase() === name.toLowerCase(),
    );
    const payload = { name, subject: subject.trim(), body: body.trim() };
    if (existing) {
      await dispatch(updateMessageTemplate({ id: existing.id, request: payload }));
    } else {
      await dispatch(createMessageTemplate(payload));
    }
  };

  const handleSend = async () => {
    try {
      const result = await dispatch(
        sendAnnouncement({
          audience,
          sessionId: audience === "Session" ? (session?.id ?? null) : null,
          subject: subject.trim(),
          body: body.trim(),
        }),
      ).unwrap();
      if (saveAsTemplate) {
        await persistTemplate();
      }
      toast.success(
        t("announcements.sentToast", "Sent to {{count}} recipients", {
          count: result.recipientCount,
        }),
      );
      onClose();
    } catch (error) {
      toast.error(
        (error as { message?: string })?.message ||
          t("announcements.sendFailed", "Sending failed"),
      );
    }
  };

  const audiencePill = (value: AnnouncementAudience, label: string) => (
    <button
      type="button"
      role="radio"
      aria-checked={audience === value}
      className={`rounded-full px-4 py-1.5 text-sm font-medium ${
        audience === value
          ? "bg-primary-95 text-primary-40 dark:bg-primary-30 dark:text-primary-90"
          : "text-grey-40 hover:bg-grey-95 dark:text-grey-70 dark:hover:bg-primary-20"
      }`}
      onClick={() => setAudience(value)}
    >
      {label}
    </button>
  );

  return (
    <ModalShell onClose={onClose} labelledBy="announcement-title">
      <div className="p-6">
        <h2 id="announcement-title" className="text-lg font-bold text-grey-20 dark:text-white">
          {t("announcements.composeTitle", "New announcement")}
        </h2>
        <p className="mt-1 text-sm text-grey-50 dark:text-grey-70">
          {t(
            "announcements.composeSubtitle",
            "Recipients get it by email in their language, with your reply address. Guardians are emailed for minors.",
          )}
        </p>

        <div className="mt-4 flex flex-wrap gap-2" role="radiogroup"
          aria-label={t("announcements.audienceLabel", "Audience")}>
          {audiencePill("AllMembers", t("announcements.audienceAll", "All members"))}
          {audiencePill("Session", t("announcements.audienceOneSession", "One session"))}
        </div>

        {audience === "Session" && (
          <div className="mt-3">
            {session ? (
              <div className="flex items-center justify-between rounded-xl bg-grey-95 px-3 py-2 dark:bg-primary-20">
                <span className="text-sm font-medium text-grey-20 dark:text-white">{session.title}</span>
                <button
                  type="button"
                  className="text-sm text-primary-40 hover:underline dark:text-primary-90"
                  onClick={() => setSession(null)}
                >
                  {t("common.change", "Change")}
                </button>
              </div>
            ) : (
              <>
                <input
                  type="text"
                  className="input"
                  placeholder={t("announcements.sessionSearchPlaceholder", "Search sessions…")}
                  value={sessionQuery}
                  onChange={(event) => setSessionQuery(event.target.value)}
                />
                {sessionResults.length > 0 && (
                  <ul className="with-border mt-1 max-h-40 overflow-y-auto rounded-xl bg-white dark:bg-primary-20">
                    {sessionResults.map((result) => (
                      <li key={result.id}>
                        <button
                          type="button"
                          className="w-full px-3 py-2 text-left text-sm text-grey-20 hover:bg-grey-95 dark:text-white dark:hover:bg-[#3E334D]"
                          onClick={() => {
                            setSession({ id: result.id, title: result.title });
                            setSessionQuery("");
                            setSessionResults([]);
                          }}
                        >
                          {result.title}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        )}

        {templates.length > 0 && (
          <div className="mt-4 flex items-center gap-2">
            <select
              className="input"
              value={templateId}
              onChange={(event) => applyTemplate(event.target.value)}
            >
              <option value="">{t("announcements.templatePlaceholder", "Start from a template…")}</option>
              {templates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name}
                </option>
              ))}
            </select>
            {templateId && (
              <button
                type="button"
                aria-label={t("announcements.deleteTemplate", "Delete template")}
                title={t("announcements.deleteTemplate", "Delete template")}
                className="text-grey-40 hover:text-red-700 dark:text-grey-80 dark:hover:text-red-400"
                onClick={() => {
                  dispatch(deleteMessageTemplate(templateId));
                  setTemplateId("");
                }}
              >
                <Trash2 size={16} />
              </button>
            )}
          </div>
        )}

        <AnnouncementAudienceSummary
          audience={audience}
          sessionId={audience === "Session" ? session?.id : undefined}
        />

        <AnnouncementMessageFields
          subject={subject}
          body={body}
          onSubjectChange={setSubject}
          onBodyChange={setBody}
          disabled={busy}
        />

        <AnnouncementPersonalization
          subject={subject}
          body={body}
          onBodyChange={setBody}
          disabled={busy}
        />

        <label className="mt-4 flex cursor-pointer items-center gap-2 text-sm text-grey-30 dark:text-grey-70">
          <input
            type="checkbox"
            checked={saveAsTemplate}
            onChange={(event) => setSaveAsTemplate(event.target.checked)}
          />
          {t("announcements.saveAsTemplate", "Save as template")}
        </label>
        {saveAsTemplate && (
          <input
            type="text"
            className="input mt-2"
            placeholder={t("announcements.templateNamePlaceholder", "Template name")}
            value={templateName}
            maxLength={100}
            onChange={(event) => setTemplateName(event.target.value)}
          />
        )}

        <div className="mt-6 flex justify-end gap-2">
          <button type="button" className="btn btn-secondary" disabled={busy} onClick={onClose}>
            {t("common.cancel", "Cancel")}
          </button>
          <button type="button" className="btn btn-primary" disabled={!canSend} onClick={handleSend}>
            {busy ? t("announcements.sending", "Sending…") : t("announcements.send", "Send")}
          </button>
        </div>
      </div>
    </ModalShell>
  );
};

export default AnnouncementComposeDialog;
